import { useState, useRef, useEffect, useContext, useCallback } from "react";
import { ToastContext } from "../../../../hooks/context";
import { uploadImage } from "../../../../services/media";
import { updateStoreDetails } from "../../../../services/inventory";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/gif"];

const useSignature = ({
  storeDetails,
  setStoreDetails,
  isEditMode,
  storeInfo,
}) => {
  const triggerToast = useContext(ToastContext);
  const [activeTab, setActiveTab] = useState("upload");
  const [isChanging, setIsChanging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadText, setUploadText] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef({ x: 0, y: 0 });

  const currentSignature = storeDetails?.signature;

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();

    if (rect.width === 0 || rect.height === 0) return;

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    ctx.scale(dpr, dpr);
    ctx.strokeStyle = "#1A1A2E";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    setHasDrawn(false);
  }, []);

  useEffect(() => {
    if (activeTab === "draw" && (!currentSignature || isChanging)) {
      const timer = setTimeout(initCanvas, 50);
      return () => clearTimeout(timer);
    }
  }, [activeTab, currentSignature, isChanging, initCanvas]);

  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX ?? (e.touches ? e.touches[0]?.clientX : 0);
    const clientY = e.clientY ?? (e.touches ? e.touches[0]?.clientY : 0);
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  };

  const handlePointerDown = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture?.(e.pointerId);
    isDrawingRef.current = true;
    const coords = getCanvasCoords(e);
    lastPointRef.current = coords;

    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };

  const handlePointerMove = (e) => {
    if (!isDrawingRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const coords = getCanvasCoords(e);

    const midX = (lastPointRef.current.x + coords.x) / 2;
    const midY = (lastPointRef.current.y + coords.y) / 2;
    ctx.quadraticCurveTo(
      lastPointRef.current.x,
      lastPointRef.current.y,
      midX,
      midY,
    );
    ctx.stroke();

    lastPointRef.current = coords;
    if (!hasDrawn) setHasDrawn(true);
  };

  const handlePointerUp = (e) => {
    if (!isDrawingRef.current) return;
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.lineTo(lastPointRef.current.x, lastPointRef.current.y);
      ctx.stroke();
      ctx.closePath();
      canvas.releasePointerCapture?.(e.pointerId);
    }
    isDrawingRef.current = false;
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const processUpload = (file, label = "Uploading signature...") => {
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      triggerToast(
        "Invalid file format. Please upload a PNG, JPG, or GIF image.",
        "warning",
      );
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      triggerToast("File size exceeds 5MB limit.", "warning");
      return;
    }

    setIsUploading(true);
    setUploadText(label);

    const formData = new FormData();
    formData.append("file", file);

    uploadImage({ formData })
      .then((res) => {
        const url = res?.data?.data?.url || res?.data?.url;
        if (!url) {
          throw new Error("No URL returned from upload");
        }

        setStoreDetails((prev) => ({ ...prev, signature: url }));

        if (isEditMode && storeInfo?.id) {
          updateStoreDetails({
            data: { signature: url },
            id: storeInfo.id,
          });
        }

        triggerToast("Signature uploaded successfully", "success");
        setIsChanging(false);
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          "Failed to upload signature. Please try again.";
        triggerToast(msg, "warning");
      })
      .finally(() => {
        setIsUploading(false);
        setUploadText("");
      });
  };

  const handleSaveDrawn = () => {
    if (!hasDrawn) {
      triggerToast("Please draw your signature before saving.", "warning");
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.toBlob((blob) => {
      if (!blob) {
        triggerToast("Failed to process signature drawing.", "warning");
        return;
      }
      const file = new File([blob], "store_signature.png", {
        type: "image/png",
      });
      processUpload(file, "Saving electronic signature...");
    }, "image/png");
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) processUpload(file);
    e.target.value = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processUpload(file);
  };

  const handleRemoveSignature = () => {
    setStoreDetails((prev) => ({ ...prev, signature: null }));

    if (isEditMode && storeInfo?.id) {
      updateStoreDetails({
        data: { signature: null },
        id: storeInfo.id,
      });
    }

    triggerToast("Signature removed", "success");
    setIsChanging(false);
  };

  const showInputArea = !currentSignature || isChanging;

  return {
    activeTab,
    setActiveTab,
    isChanging,
    setIsChanging,
    isUploading,
    uploadText,
    isDragging,
    hasDrawn,
    fileInputRef,
    canvasRef,
    currentSignature,
    showInputArea,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    clearCanvas,
    handleSaveDrawn,
    handleFileChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleRemoveSignature,
  };
};

export default useSignature;
